import * as React from "react";
import {
  Accordion, AccordionSummary, AccordionDetails,
  Box, Button, FormControl, Grid, TextField, Typography
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AddCircleOutline, RemoveCircleOutline } from "@mui/icons-material";
import { useStyles } from "../../../../Theme/Style";
import { useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useNotification } from "../../../../Contexts/GlobalNotificationContext";
import {
  useGetTrimDetailsQuery,
  useSubmitTrimDetailsMutation,
} from "../../../../services/api/production-order";
import { useGetTrimTypeQuery } from "../../../../services/api/trim-types";

const TrimDetailsComponent = ({ prodId, templateItems = [] }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const { data: trimData, isLoading: isLoadingTrims } = useGetTrimDetailsQuery({ id: prodId });
  const [submitTrimDetails] = useSubmitTrimDetailsMutation();
  const { data: trimTypeResp, isLoading: isLoadingTrimTypes } = useGetTrimTypeQuery();

  // ---- helpers / options ----
  const allTrimTypes = useMemo(() => {
    if (!trimTypeResp) return [];
    if (Array.isArray(trimTypeResp)) return trimTypeResp;
    if (Array.isArray(trimTypeResp?.data)) return trimTypeResp.data;
    if (Array.isArray(trimTypeResp?.trim_types)) return trimTypeResp.trim_types;
    return [];
  }, [trimTypeResp]);

  const allColors = useMemo(() => trimData?.colors ?? [], [trimData]);
  const allItems  = useMemo(() => trimData?.items  ?? [], [trimData]);
  const allPositions = useMemo(() => trimData?.positions ?? [], [trimData]);

  // Local rows: { item, trimType, color, position }
  const [trimItems, setTrimItems] = useState([]);

  const labelForItem      = (o) => (o ? `${o.name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForColor     = (o) => (o ? `${o.english_name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForPosition  = (o) => (o ? `${o.name} - ${o.chinese_name || ""}`.trim() : "");
  const labelForTrimType  = (o) => (o ? `${o.name || o.technical_name || ""}${o.chinese_name ? " - " + o.chinese_name : ""}`.trim() : "");

  const normalizeByIds = (idsOrObjs, universe) => {
    if (!Array.isArray(idsOrObjs) || idsOrObjs.length === 0) return [];
    const primitives = idsOrObjs.every((x) => ["number", "string"].includes(typeof x));
    if (primitives) {
      const idSet = new Set(idsOrObjs.map(String));
      return (universe || []).filter((u) => idSet.has(String(u.id)));
    }
    // assume already objects; keep those that exist in universe (when id matches), else pass-through
    const byId = new Map((universe || []).map((u) => [String(u.id), u]));
    return idsOrObjs.map((x) => byId.get(String(x?.id)) || x);
  };

  // 🔹 Colors scoping per row (from templateItems[rowIndex].colors)
  const getColorOptionsForRow = (rowIndex) => {
    const tItem = templateItems?.[rowIndex];
    const scoped = normalizeByIds(tItem?.colors, allColors);
    return scoped.length ? scoped : allColors;
  };

  // 🔹 Trim types scoping (you already had this for types)
  const getTrimTypeOptionsForRow = (rowIndex) => {
    const tItem = templateItems?.[rowIndex];
    const scoped = normalizeByIds(tItem?.trimType, allTrimTypes);
    return scoped.length ? scoped : allTrimTypes;
  };

  // Prefill rows from templateItems or existing trims
  useEffect(() => {
    if (!trimData) return;

    if (templateItems.length) {
      const rows = templateItems.map((t, i) => {
        const item     = allItems.find((x) => String(x.id) === String(t.itemId)) || null;
        const trimType = allTrimTypes.find((tt) => String(tt.id) === String(t.trimTypeId)) || null;

        // Preselect color if colorId present
        const colorOptions = getColorOptionsForRow(i);
        const color = colorOptions.find((c) => String(c.id) === String(t.colorId)) || null;

        // Preselect position if provided (positionId from your screenshot)
        const position =
          allPositions.find((p) => String(p.id) === String(t.positionId)) || null;

        return { item, trimType, color, position };
      });

      setTrimItems(rows.length ? rows : [{ item: null, trimType: null, color: null, position: null }]);
      return;
    }

    if (Array.isArray(trimData?.trims) && trimData.trims.length) {
      const rows = trimData.trims.map((t) => ({
        item: t.item || null,
        trimType: t.trim_type || null,
        color: t.color || null,
        position: t.position || null,
      }));
      setTrimItems(rows);
      return;
    }

    setTrimItems([{ item: null, trimType: null, color: null, position: null }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimData, templateItems, allTrimTypes, allColors, allItems, allPositions]);

  const addNewRow = () =>
    setTrimItems((prev) => [...prev, { item: null, trimType: null, color: null, position: null }]);

  const handleChange = (_e, fieldName, index, value) => {
    setTrimItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [fieldName]: value ?? null };

      // If user changes trim item or trim type, you might want to clear color if it’s no longer in scoped options
      if (fieldName === "item" || fieldName === "trimType") {
        const colorOptions = getColorOptionsForRow(index);
        if (copy[index].color && !colorOptions.some((c) => String(c.id) === String(copy[index].color?.id))) {
          copy[index].color = null;
        }
      }

      return copy;
    });
  };

  const deleteRow = (index) =>
    setTrimItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));

  const handleSubmit = async () => {
    try {
      const isValid = trimItems.every((r) => r?.item?.id && r?.trimType?.id && r?.color?.id && r?.position?.id);
      if (!isValid) throw new Error("Please fill out all fields in each row.");

      await submitTrimDetails({
        production_order_id: prodId,
        trims: trimItems,
      }).unwrap();

      addNotification({ message: "Trim details submitted successfully!", type: "success" });
    } catch (error) {
      addNotification({ message: error?.message || "Failed to submit trim details.", type: "error" });
    }
  };

  const loading = isLoadingTrims || isLoadingTrimTypes;

  return (
    <Accordion className={classes.accordion}>
      <AccordionSummary
        className={classes.accordionSummary}
        expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
      >
        <Grid container className={classes.accordionSummaryTextContainer}>
          <Grid item xs={4}>Trim Details</Grid>
          <Grid item xs={4}>修剪细节</Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails>
        <Box className={classes.tableHeader}>
          <Grid container>
            <Grid item xs={3}><Typography>Item</Typography><Typography>物品</Typography></Grid>
            <Grid item xs={3}><Typography>Trim Type</Typography><Typography>修剪类型</Typography></Grid>
            <Grid item xs={3}><Typography>Color</Typography><Typography>颜色</Typography></Grid>
            <Grid item xs={3}><Typography>Position</Typography><Typography>位置</Typography></Grid>
          </Grid>
        </Box>

        {trimItems.map((row, index) => {
          const trimTypeOptions = getTrimTypeOptionsForRow(index);
          const colorOptions = getColorOptionsForRow(index);

          return (
            <Box key={index} className={classes.row} sx={{ mt: 1 }}>
              <Grid container spacing={2} alignItems="center">
                {/* Item */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={allItems}
                      getOptionLabel={labelForItem}
                      value={row.item}
                      loading={loading}
                      onChange={(e, v) => handleChange(e, "item", index, v)}
                      renderInput={(params) => <TextField {...params} placeholder="Select Item" variant="standard" />}
                      isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    />
                  </FormControl>
                </Grid>

                {/* Trim Type */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={trimTypeOptions}
                      getOptionLabel={labelForTrimType}
                      value={
                        row.trimType
                          ? trimTypeOptions.find((t) => String(t?.id) === String(row.trimType?.id)) || row.trimType
                          : null
                      }
                      loading={loading}
                      onChange={(e, v) => handleChange(e, "trimType", index, v)}
                      renderInput={(params) => <TextField {...params} placeholder="Select Trim Type" variant="standard" />}
                      isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    />
                  </FormControl>
                </Grid>

                {/* Color (scoped per template row) */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={colorOptions}
                      getOptionLabel={labelForColor}
                      value={
                        row.color
                          ? colorOptions.find((c) => String(c?.id) === String(row.color?.id)) || row.color
                          : null
                      }
                      loading={loading}
                      onChange={(e, v) => handleChange(e, "color", index, v)}
                      renderInput={(params) => <TextField {...params} placeholder="Select Color" variant="standard" />}
                      isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    />
                  </FormControl>
                </Grid>

                {/* Position */}
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <Autocomplete
                      options={allPositions}
                      getOptionLabel={labelForPosition}
                      value={row.position}
                      loading={loading}
                      onChange={(e, v) => handleChange(e, "position", index, v)}
                      renderInput={(params) => <TextField {...params} placeholder="Select Position" variant="standard" />}
                      isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12} sx={{ textAlign: "right" }}>
                  <Button
                    onClick={() => deleteRow(index)}
                    disabled={trimItems.length === 1}
                    startIcon={<RemoveCircleOutline />}
                    color="error"
                    variant="outlined"
                  >
                    Remove
                  </Button>
                </Grid>
              </Grid>
            </Box>
          );
        })}

        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={addNewRow} startIcon={<AddCircleOutline />} disabled={loading}>
            Add New Row
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={loading || !trimItems.length}>
            Save
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default TrimDetailsComponent;
